import {preparationProof} from "./preparationAccess";
import {requestJson} from './requestJson';
import type { Vehicle } from "../types";
import { demoFetch, isDemoApiBase } from "./demoApi";
import type { Preferences } from "./preferences";

export type AccountProfile = { id: string; email: string; firstName: string; lastName: string; language: string; preferences?: Partial<Preferences>|null; role: string; phone: string; address: string; profilePhoto: string; notificationsEnabled: boolean; emailVerified: boolean };
export function saveAccountPreferences(apiBase:string,preferences:Partial<Preferences>){
 return securityJson<{updated:true;account:AccountProfile}>(apiBase,'/api/v1/preferences',{method:'PATCH',headers:{'content-type':'application/json'},body:JSON.stringify(preferences)});
}
export type AccountSession = { id: string; createdAt: string; expiresAt: string; lastSeenAt: string; userAgent: string | null; current: boolean };
export type SecurityEvent = { id: string; eventType: string; severity: string; userAgent: string | null; country: string | null; details: Record<string, unknown>; createdAt: string };
export type SecurityOverview = { twoFactorEnabled: boolean; twoFactorEnabledAt: string | null; recoveryCodesRemaining: number; suspiciousAlertsEnabled: boolean; passwordChangedAt: string | null; events: SecurityEvent[] };
export type GdprRequest = { id: string; status: string; requestedAt: string; completedAt: string | null; downloadedAt: string | null };
export type GdprBundle = { generatedAt: string; profile: Record<string, unknown>; vehicles: Record<string, unknown>[]; appointments: Record<string, unknown>[]; approvals: Record<string, unknown>[]; conversations: Record<string, unknown>[]; documents: { vehicleDocuments: Record<string, unknown>[]; attachments: Array<{ id: string; requestId: string; fileName: string; contentType: string; sizeBytes: number; createdAt: string; downloadPath: string }> } };
export type VehicleDriver = { id: string; role: string; name: string; email: string; canManageServices: boolean; canEditVehicle: boolean; createdAt: string };
export type VehicleDriverInvite = { id: string; targetEmail: string | null; inviteUrl?: string; canManageServices: boolean; canEditVehicle: boolean; expiresAt: string; createdAt?: string };
export type AssistanceAttachment = { id: string; name: string; mime: string; size: number; url: string };
export type AssistanceMessage = { id: string; sender: "CUSTOMER" | "WORKSHOP" | "SYSTEM"; body: string; attachments?: AssistanceAttachment[]; createdAt: string; readAt?: string | null };
export type AssistanceThread = { id: string; vehicleId: string; userId: string; status: string; category: string; subject: string; dossierId: string | null; dossierNumber: string | null; syncStatus: "SYNCED" | "PENDING"; createdAt: string; updatedAt: string };
export type AssistanceWorkshop = { name: string; address: string; phone: string; email: string; hours: string };
export type AssistanceOverview = { thread: AssistanceThread | null; messages: AssistanceMessage[]; workshop: AssistanceWorkshop; dossier?: Record<string, unknown> | null };
export type InventoryVehicle = {
  id: string;
  title: string;
  variant: string;
  location: string;
  price: string;
  leasing: string;
  leasingTerms?: { enabled: boolean; monthlyPaymentChf: number | null; downPaymentChf: number | null; downPaymentPercent: number; termMonths: number | null; annualKm: number | null; residualValueChf: number | null; residualPercent: number; interestRatePercent: number | null; creditInterestRatePercent: number; setupFeeChf: number | null; provider: string; notes: string };
  image: string;
  url: string;
  vin: string;
  range: string;
  topSpeed: string;
  acceleration: string;
  extras: string[];
  autopilot: string;
  mileage: string;
  firstRegistration: string;
  hardware: string;
  soh: string;
  tvpf: null | { progress: number; status: string; completed: number; total: number; photos: number; reportUrl: string };
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function normalizeAssistanceOverview<T extends Record<string, unknown>>(payload: T): T & AssistanceOverview {
  const rawThread = isRecord(payload.thread) ? payload.thread : null;
  const rawWorkshop = isRecord(payload.workshop) ? payload.workshop : {};
  const rawMessages = Array.isArray(payload.messages) ? payload.messages : [];
  const messages = rawMessages.flatMap((entry, index): AssistanceMessage[] => {
    if (!isRecord(entry)) return [];
    const rawSender = String(entry.sender || "WORKSHOP").toUpperCase();
    const sender: AssistanceMessage["sender"] = rawSender === "CUSTOMER" || rawSender === "SYSTEM" ? rawSender : "WORKSHOP";
    const rawAttachments = Array.isArray(entry.attachments) ? entry.attachments : [];
    const attachments = rawAttachments.flatMap((file, fileIndex): AssistanceAttachment[] => {
      if (!isRecord(file)) return [];
      return [{
        id: String(file.id || `${String(entry.id || index)}-attachment-${fileIndex}`),
        name: String(file.name || file.caption || "Documento"),
        mime: String(file.mime || file.type || "application/octet-stream"),
        size: Number.isFinite(Number(file.size)) ? Number(file.size) : 0,
        url: String(file.url || ""),
      }];
    });
    return [{
      id: String(entry.id || `message-${index}`),
      sender,
      body: String(entry.body || ""),
      attachments,
      createdAt: String(entry.createdAt || entry.created_at || ""),
      readAt: entry.readAt || entry.read_at ? String(entry.readAt || entry.read_at) : null,
    }];
  });
  const thread: AssistanceThread | null = rawThread ? {
    id: String(rawThread.id || ""),
    vehicleId: String(rawThread.vehicleId || rawThread.vehicle_id || ""),
    userId: String(rawThread.userId || rawThread.user_id || ""),
    status: String(rawThread.status || "OPEN"),
    category: String(rawThread.category || "OTHER"),
    subject: String(rawThread.subject || "Pedido de assistência"),
    dossierId: rawThread.dossierId == null && rawThread.dossier_id == null ? null : String(rawThread.dossierId || rawThread.dossier_id),
    dossierNumber: rawThread.dossierNumber == null && rawThread.dossier_number == null ? null : String(rawThread.dossierNumber || rawThread.dossier_number),
    syncStatus: String(rawThread.syncStatus || rawThread.sync_status || "PENDING").toUpperCase() === "SYNCED" ? "SYNCED" : "PENDING",
    createdAt: String(rawThread.createdAt || rawThread.created_at || ""),
    updatedAt: String(rawThread.updatedAt || rawThread.updated_at || rawThread.createdAt || rawThread.created_at || ""),
  } : null;
  const workshop: AssistanceWorkshop = {
    name: String(rawWorkshop.name || "Precision Autotechnik AG"),
    address: String(rawWorkshop.address || "Wattwerkstrasse 2, 4416 Bubendorf"),
    phone: String(rawWorkshop.phone || "+41 61 599 28 00"),
    email: String(rawWorkshop.email || "info@precisionautotechnik.com"),
    hours: String(rawWorkshop.hours || "Seg.–Sex. 07:30–17:30 · Sáb. 09:00–12:00"),
  };
  return {
    ...payload,
    thread,
    messages,
    workshop,
    dossier: isRecord(payload.dossier) ? payload.dossier : null,
  };
}

const AUTH_TOKEN_KEY = "precisionEV.authToken.v1";

export function getAuthToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY) || "";
}

export function rememberAuthToken(token: string) {
  if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function forgetAuthToken() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

export async function exchangeDriveHandoff(apiBase: string, handoff: string) {
  const response = await fetch(apiUrl(apiBase, "/api/v1/auth/drive-handoff/exchange"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ handoff }),
    cache: "no-store",
    credentials: "include",
    referrerPolicy: "no-referrer",
  });
  if (!response.ok) throw new Error("DRIVE_HANDOFF_FAILED");
  const payload = await response.json() as { authenticated?: boolean; token?: string };
  if (!payload.authenticated || !payload.token) throw new Error("DRIVE_HANDOFF_FAILED");
  rememberAuthToken(payload.token);
}

import {customerPreviewFetch} from './customerPreview';
import {supportPreviewFetch} from './supportPreview';

export function apiUrl(apiBase: string, path: string) {
  const base = apiBase.replace(/\/$/, "");
  if (/\/index\.php$/i.test(base)) return `${base}?path=${encodeURIComponent(path)}`;
  return `${base}${path}`;
}

export async function accountFetch(apiBase: string, path: string, init: RequestInit = {}) {
  if(apiBase.startsWith('preview:support'))return supportPreviewFetch(path,init);
  if(apiBase.startsWith('preview:'))return customerPreviewFetch(path,init);
  if (apiBase.startsWith("guest:")) {
    const token=apiBase.slice(6),headers=new Headers(init.headers);
    headers.set("X-Service-Token",token);
    const response=await fetch(apiUrl(import.meta.env.VITE_API_BASE_URL||"https://precisionautotechnik.synology.me:8443/precision-ev-api/index.php","/api/v1/guest"+path.replace(/^\/api\/v1/,"")),{...init,headers,cache:"no-store",referrerPolicy:"no-referrer"});
    if(response.ok){const payload=await response.clone().json().catch(()=>null);if(payload?.error&&payload?.httpStatus>=400){const logical=new Response(JSON.stringify(payload),{status:payload.httpStatus,headers:{"Content-Type":"application/json"}});if(logical.status===410)window.dispatchEvent(new Event("precision-guest-expired"));return logical;}}
    if(response.status===410)window.dispatchEvent(new Event("precision-guest-expired"));
    return response;
  }
  if (isDemoApiBase(apiBase)) return demoFetch(path, init);
  const headers = new Headers(init.headers);
  const preparationToken=headers.get("X-Service-Token");
  if(preparationToken){const proof=preparationProof(preparationToken);if(proof)headers.set("X-Preparation-Access",proof);}
  const token = getAuthToken();
  if (token) headers.set("authorization", `Bearer ${token}`);
  const controller = new AbortController();
  const abort = () => controller.abort();
  const timeout = window.setTimeout(abort, path==='/api/v1/preparation/access/send'?30_000:10_000);
  if (init.signal) {
    if (init.signal.aborted) controller.abort();
    else init.signal.addEventListener("abort", abort, { once: true });
  }
  try {
    return await fetch(apiUrl(apiBase, path), { ...init, headers, credentials: "include", signal: controller.signal });
  } finally {
    window.clearTimeout(timeout);
    init.signal?.removeEventListener("abort", abort);
  }
}

export function accountJson<T>(apiBase:string,path:string,init:RequestInit={}){
 return requestJson<T>(signal=>accountFetch(apiBase,path,{...init,signal}),init.signal);
}

export async function restoreAccountSession(apiBase: string) {
  if (!isDemoApiBase(apiBase) && !apiBase.startsWith('preview:support') && !getAuthToken()) return null;
  const response = await accountFetch(apiBase, "/api/v1/session", { cache: "no-store" });
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      forgetAuthToken();
      return null;
    }
    throw new Error("ACCOUNT_API_UNAVAILABLE");
  }
  return response.json() as Promise<{ authenticated: true; account: AccountProfile }>;
}

export async function saveAccountProfile(apiBase: string, profile: { fullName: string; phone: string; address: string; profilePhoto: string; notificationsEnabled: boolean }) {
  const response = await accountFetch(apiBase, "/api/v1/profile", {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(profile),
  });
  if (!response.ok) throw new Error("PROFILE_SAVE_FAILED");
  return response.json() as Promise<{ updated: true; account: AccountProfile }>;
}

export async function resetPrecisionProfile(apiBase: string, currentPassword: string) {
  const response = await accountFetch(apiBase, "/api/v1/profile/reset", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ currentPassword }),
  });
  const payload = await response.json().catch(() => ({})) as { reset?: true; backupId?: string; resetAt?: string; account?: AccountProfile; error?: string };
  if (!response.ok || !payload.reset || !payload.account) throw new Error(payload.error || "PROFILE_RESET_FAILED");
  return payload as { reset: true; backupId: string; resetAt: string; account: AccountProfile };
}

export async function deleteAccount(apiBase: string, currentPassword: string, confirmation: string) {
  const response = await accountFetch(apiBase, "/api/v1/account/delete", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ currentPassword, confirmation }),
  });
  const payload = await response.json().catch(() => ({})) as { deleted?: true; dataRetained?: true; deletedAt?: string; error?: string };
  if (!response.ok || !payload.deleted) throw new Error(payload.error || "ACCOUNT_DELETE_FAILED");
  if(!isDemoApiBase(apiBase))forgetAuthToken();
  return payload as { deleted: true; dataRetained: true; deletedAt: string };
}

export async function loadAccountVehicles(apiBase: string): Promise<Vehicle[]> {
  const response = await accountFetch(apiBase, "/api/v1/vehicles", { cache: "no-store" });
  if (!response.ok) throw new Error("VEHICLES_UNAVAILABLE");
  const payload = await response.json() as { vehicles?: Vehicle[] };
  return (payload.vehicles || []).map((vehicle) => ({ ...vehicle, events: vehicle.events || [], batteryMeasurements: vehicle.batteryMeasurements || [] }));
}

export async function loadPublicInventory(apiBase: string): Promise<InventoryVehicle[]> {
  const response = await accountFetch(apiBase, "/api/v1/public/inventory", { cache: "no-store" });
  const payload = await response.json().catch(() => ({})) as { vehicles?: InventoryVehicle[]; error?: string };
  if (!response.ok || !Array.isArray(payload.vehicles)) throw new Error(payload.error || "INVENTORY_UNAVAILABLE");
  return payload.vehicles;
}

export async function saveAccountVehicles(apiBase: string, vehicles: Vehicle[]) {
  const response = await accountFetch(apiBase, "/api/v1/vehicles", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ vehicles }),
  });
  if (!response.ok) throw new Error("VEHICLES_SAVE_FAILED");
}

export async function linkTvpfVehicle(apiBase: string, token: string) {
  const response = await accountFetch(apiBase, "/api/v1/vehicles/tvpf/link", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  });
  const payload = await response.json().catch(() => ({})) as { linked?: true; refreshed?: boolean; vehicleId?: string; error?: string };
  if (!response.ok || !payload.linked || !payload.vehicleId) throw new Error(payload.error || "TVPF_LINK_FAILED");
  return payload as { linked: true; refreshed: boolean; vehicleId: string };
}

export function requestTvpfPickup(apiBase: string, vehicleId: string, preferredAt: string, note: string) {
  return securityJson<{ updated: true; vehicle: Vehicle }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/tvpf/pickup`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ operation: "request", preferredAt, note }) });
}

export function acceptTvpfPickupAlternative(apiBase: string, vehicleId: string) {
  return securityJson<{ updated: true; vehicle: Vehicle }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/tvpf/pickup`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ operation: "accept_alternative" }) });
}

export function linkDossierVehicle(apiBase: string, token: string) {
  return securityJson<{ linked: true; vehicleId: string }>(apiBase, "/api/v1/vehicles/dossier/link", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token }) });
}

export async function loadAccountSessions(apiBase: string): Promise<AccountSession[]> {
  const response = await accountFetch(apiBase, "/api/v1/security/sessions", { cache: "no-store" });
  if (!response.ok) throw new Error(response.status === 401 ? "UNAUTHENTICATED" : "SESSIONS_UNAVAILABLE");
  const payload = await response.json() as { sessions?: AccountSession[] };
  return payload.sessions || [];
}

export async function terminateAccountSession(apiBase: string, sessionId: string) {
  const response = await accountFetch(apiBase, `/api/v1/security/sessions/${encodeURIComponent(sessionId)}`, { method: "DELETE" });
  if (!response.ok) throw new Error(response.status === 401 ? "UNAUTHENTICATED" : "SESSION_TERMINATION_FAILED");
  return response.json() as Promise<{ terminated: true; currentTerminated: boolean }>;
}

export async function terminateAllAccountSessions(apiBase: string) {
  const response = await accountFetch(apiBase, "/api/v1/security/sessions", { method: "DELETE" });
  if (!response.ok) throw new Error(response.status === 401 ? "UNAUTHENTICATED" : "SESSION_TERMINATION_FAILED");
  return response.json() as Promise<{ terminatedAll: true }>;
}

async function securityJson<T>(apiBase: string, path: string, init: RequestInit = {}) {
  const response = await accountFetch(apiBase, path, init);
  const payload = await response.json().catch(() => ({})) as T & { error?: string };
  if (!response.ok) throw new Error(payload.error || "SECURITY_REQUEST_FAILED");
  return payload;
}

export function loadVehicleDrivers(apiBase: string, vehicleId: string) {
  return securityJson<{ drivers: VehicleDriver[]; invites: VehicleDriverInvite[] }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/drivers`, { cache: "no-store" });
}

export function createVehicleDriverInvite(apiBase: string, vehicleId: string, options: { email?: string; canManageServices: boolean; canEditVehicle: boolean }) {
  return securityJson<{ invite: VehicleDriverInvite }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/drivers`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(options) });
}

export function updateVehicleDriverPermissions(apiBase: string, vehicleId: string, accessId: string, permissions: { canManageServices: boolean; canEditVehicle: boolean }) {
  return securityJson<{ updated: true }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/drivers/${encodeURIComponent(accessId)}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(permissions) });
}

export function revokeVehicleDriver(apiBase: string, vehicleId: string, accessId: string) {
  return securityJson<{ revoked: true }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/drivers/${encodeURIComponent(accessId)}`, { method: "DELETE" });
}

export function cancelVehicleDriverInvite(apiBase: string, vehicleId: string, inviteId: string) {
  return securityJson<{ cancelled: true }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/driver-invites/${encodeURIComponent(inviteId)}`, { method: "DELETE" });
}

export function acceptVehicleDriverInvite(apiBase: string, token: string) {
  return securityJson<{ accepted: true; vehicleId: string }>(apiBase, "/api/v1/vehicle-invites/accept", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token }) });
}

export function startVehicleTransfer(apiBase: string, vehicleId: string, details: { firstName: string; lastName: string; email: string; emailConfirmation: string; buyerType: "INDIVIDUAL" | "THIRD_PARTY" }) {
  return securityJson<{ transferId: string; email: string; confirmationSentTo: "CURRENT_OWNER"; expiresAt: string }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/transfer/start`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(details) });
}

export function confirmVehicleTransfer(apiBase: string, vehicleId: string, code: string) {
  return securityJson<{ transferred: true; accountExists: boolean; reservedForEmail: boolean; email: string }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/transfer/confirm`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ code }) });
}

export function removeVehicleFromAccount(apiBase: string, vehicleId: string) {
  return securityJson<{ removed: true; dataRetained: true }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/remove`, { method: "POST" });
}

export function loadVehicleAssistance(apiBase: string, vehicleId: string) {
  return securityJson<Record<string, unknown>>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/assistance`, { cache: "no-store" }).then(normalizeAssistanceOverview);
}

export function createVehicleAssistanceRequest(apiBase: string, vehicleId: string, request: { category: "MAINTENANCE" | "DIAGNOSIS" | "PREPARATION" | "OTHER"; subject: string; body: string; mileage?: number; termsAcceptedAt: string; signature: string }) {
  return securityJson<Record<string, unknown> & { created: true; synced: boolean }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/assistance/requests`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then(normalizeAssistanceOverview);
}

export function sendVehicleAssistanceMessage(apiBase: string, vehicleId: string, body: string) {
  return securityJson<Record<string, unknown> & { created: true; synced: boolean }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/assistance/messages`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ body }) }).then(normalizeAssistanceOverview);
}

export function markAssistanceMessagesRead(apiBase:string,vehicleId:string,threadId:string,messageIds:string[]) {
  return securityJson<{readMessageIds:string[]}>(apiBase,`/api/v1/vehicles/${encodeURIComponent(vehicleId)}/assistance/read`,{method:"POST",headers:{"content-type":"application/json"},body:JSON.stringify({threadId,messageIds})});
}

export function sendVehicleAssistanceAttachment(apiBase: string, vehicleId: string, file: File, body = "") {
  const form = new FormData();
  form.append("file", file);
  form.append("body", body);
  return securityJson<Record<string, unknown> & { created: true; synced: boolean }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/assistance/attachments`, { method: "POST", body: form }).then(normalizeAssistanceOverview);
}

export function sendVehicleDossierDecision(apiBase: string, vehicleId: string, decisionType: "quote" | "diagnostic" | "initial", reference: string, decision: "approved" | "rejected" | "contact") {
  return securityJson<Record<string, unknown> & { updated: true }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/dossier/decision`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ decisionType, reference, decision }) }).then(normalizeAssistanceOverview);
}

export function acceptVehicleDossierTerms(apiBase: string, vehicleId: string, request: { termsAcceptedAt: string; signature: string }) {
  return securityJson<Record<string, unknown> & { updated: true }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/dossier/acceptance`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then(normalizeAssistanceOverview);
}

export function sendVehicleDossierAction(apiBase: string, vehicleId: string, serviceAction: "appointment_confirm" | "appointment_reschedule" | "appointment_cancel" | "rental_cancel" | "after_hours_request" | "dropoff_method" | "dropoff_confirm" | "payment_choice" | "pickup_method" | "availability_update" | "delivery_estimate_update", value = "") {
  return securityJson<Record<string, unknown> & { updated: true }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/dossier/action`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ serviceAction, value }) }).then(normalizeAssistanceOverview);
}

export function requestVehicleDossierChange(apiBase: string, vehicleId: string, kind: "initial" | "diagnostic" | "quote" | "repair" | "appointment", reference: string, body: string) {
  return securityJson<Record<string, unknown> & { updated: true }>(apiBase, `/api/v1/vehicles/${encodeURIComponent(vehicleId)}/dossier/change-request`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ kind, reference, body }) }).then(normalizeAssistanceOverview);
}

export function advanceDemoWorkshop(apiBase: string, vehicleId: string) {
  return securityJson<Record<string, unknown> & { updated: true; demoStage: string }>(apiBase, `/api/v1/demo/vehicles/${encodeURIComponent(vehicleId)}/workshop/advance`, { method: "POST" }).then(normalizeAssistanceOverview);
}

export function sendDemoWorkshopMessage(apiBase: string, vehicleId: string, body: string) {
  return securityJson<Record<string, unknown> & { created: true }>(apiBase, `/api/v1/demo/vehicles/${encodeURIComponent(vehicleId)}/workshop/message`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ body }) }).then(normalizeAssistanceOverview);
}

export function loadSecurityOverview(apiBase: string) {
  return securityJson<SecurityOverview>(apiBase, "/api/v1/security", { cache: "no-store" });
}

export function changeAccountPassword(apiBase: string, currentPassword: string, newPassword: string) {
  return securityJson<{ changed: true; otherSessionsTerminated: true; changedAt: string }>(apiBase, "/api/v1/security/password", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ currentPassword, newPassword }) });
}

export function startTwoFactorSetup(apiBase: string, currentPassword: string) {
  return securityJson<{ secret: string; uri: string; expiresAt: string }>(apiBase, "/api/v1/security/2fa/setup", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ currentPassword }) });
}

export function enableTwoFactor(apiBase: string, code: string) {
  return securityJson<{ enabled: true; enabledAt: string; recoveryCodes: string[] }>(apiBase, "/api/v1/security/2fa/enable", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ code }) });
}

export function disableTwoFactor(apiBase: string, currentPassword: string, code: string) {
  return securityJson<{ disabled: true }>(apiBase, "/api/v1/security/2fa/disable", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ currentPassword, code }) });
}

export function regenerateRecoveryCodes(apiBase: string, currentPassword: string, code: string) {
  return securityJson<{ recoveryCodes: string[] }>(apiBase, "/api/v1/security/2fa/recovery-codes", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ currentPassword, code }) });
}

export function setSuspiciousAlerts(apiBase: string, enabled: boolean) {
  return securityJson<{ enabled: boolean }>(apiBase, "/api/v1/security/alerts", { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ enabled }) });
}

export function loadGdprRequests(apiBase: string) {
  return securityJson<{ requests: GdprRequest[] }>(apiBase, "/api/v1/privacy/exports", { cache: "no-store" });
}

export function createGdprExport(apiBase: string) {
  return securityJson<{ request: GdprRequest; bundle: GdprBundle }>(apiBase, "/api/v1/privacy/exports", { method: "POST" });
}

export function markGdprExportDownloaded(apiBase: string, requestId: string) {
  return securityJson<{ downloaded: true; downloadedAt: string }>(apiBase, `/api/v1/privacy/exports/${encodeURIComponent(requestId)}/downloaded`, { method: "POST" });
}

export async function logoutAccount(apiBase: string) {
  try {
    await accountFetch(apiBase, "/api/v1/auth/logout", { method: "POST" });
  } finally {
    if(!isDemoApiBase(apiBase))forgetAuthToken();
  }
}
